import { useState } from 'react'
import { useMeta, useWorkspaces } from '@/db/hooks'
import { META } from '@/db/schema'
import { pushSupported, subscribePush, unsubscribePush } from '@/push'
import { db, engine } from '@/runtime'
import { useAccess } from '@/state/access'
import { Button, PageTitle } from './ui'

/**
 * Settings for a workspace member: who they are signed in as and a way out.
 * Everything else in Settings is the owner's (the server's keys, devices,
 * the assistant, sharing), and their token cannot reach it.
 */
export const MemberAccount = ({ embedded = false }: { embedded?: boolean }) => {
  const admin = useAccess((s) => s.admin)
  const name = useAccess((s) => s.name)
  const workspaces = useWorkspaces().filter((w) => !w.deleted_at)
  const signOut = async () => {
    if (!confirm('Sign out of this device? What you have here is removed; it stays shared with you.')) return
    engine.stop()
    try {
      await unsubscribePush()
    } catch {
      /* best effort */
    }
    await db.delete()
    try {
      localStorage.removeItem('settings-cache')
    } catch {
      /* nothing kept */
    }
    window.location.replace('/')
  }
  const subscribed = useMeta<boolean>(META.pushSubscribed) ?? false
  const [pushMsg, setPushMsg] = useState<string | null>(null)
  const togglePush = async () => {
    setPushMsg(null)
    try {
      if (subscribed) {
        await unsubscribePush()
        setPushMsg('Notifications off on this device.')
      } else {
        await subscribePush()
        setPushMsg('Notifications on for this device.')
      }
    } catch (e) {
      setPushMsg((e as Error).message)
    }
  }
  return (
    <div className="pb-20 md:pb-0" data-testid="member-account">
      {!embedded && <PageTitle>Settings</PageTitle>}
      <section className="mb-6">
        <h2 className="text-sm font-medium mb-1">
          {admin && name ? `Signed in as ${name}` : 'Shared with you'}
        </h2>
        <p className="text-xs text-faint mb-2" data-testid="member-reach">
          {admin
            ? 'You are an admin: the whole app, and your own look and layout, which never change anyone else’s. Tasks, projects, pages and the rest stay shared. The owner keeps the dev suite, agents, the factory, tokens, security and wellbeing. They can end this any time; this device then stops syncing.'
            : `You are signed in with an invitation. You see and can change what is in ${workspaces.length ? workspaces.map((w) => w.spec.name).join(', ') : 'the shared suite'}, and nothing else of the owner’s. They can end it any time; this device then stops syncing.`}
        </p>
      </section>
      <section className="mb-6">
        <h2 className="text-sm font-medium mb-1">Notifications</h2>
        <p className="text-xs text-faint mb-2">
          Told when someone finishes or moves something you added, or it goes late.
        </p>
        {pushSupported() ? (
          <>
            <Button onClick={() => void togglePush()} data-testid="member-push">
              {subscribed ? 'Turn notifications off' : 'Turn notifications on'}
            </Button>
            {pushMsg && <p className="text-xs text-faint mt-2">{pushMsg}</p>}
          </>
        ) : (
          // an iPhone has web push only in an installed app, so a browser tab gets the way there
          <p className="text-sm text-muted" data-testid="member-push-unsupported">
            This browser can&apos;t show notifications here. On an iPhone, open this page in Safari, tap
            Share, then Add to Home Screen, and open the app from there.
          </p>
        )}
      </section>
      <section>
        <Button onClick={() => void signOut()} data-testid="member-signout">
          Sign out of this device
        </Button>
      </section>
    </div>
  )
}
